import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";
import { Tooltip } from "../../../../../common/components/Tooltip/Tooltip";

interface Answer {
  text?: string;
  imageId?: number;
  is_right: boolean;
}

interface Question {
  text: string;
  answers: Answer[];
  winText?: string;
  loseText?: string;
  imageAnswers: boolean;
  allowMultiple?: boolean;
}

interface RandomQuizzContentData {
  questions: Question[];
  nb_questions: number;
}

interface UserAnswer {
  questionIndex: number;
  answersIndex: number[];
  validationTime?: number;
}

interface AnswerWithImage extends Answer {
  index: number;
  imageUrl?: string;
}

interface RandomQuizzDetailProps {
  data: {
    list_answer_random_quizz?: UserAnswer[];
    score?: number;
  };
  contentId?: number;
}

const RandomQuizzDetail = ({ data, contentId }: RandomQuizzDetailProps) => {
  const { t } = useTranslation();
  const [quizzData, setQuizzData] = useState<RandomQuizzContentData | null>(null);
  const [imageUrls, setImageUrls] = useState<Map<number, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const userAnswers = data?.list_answer_random_quizz || [];

  // Fetch quiz content and images
  useEffect(() => {
    const fetchData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        // Fetch random quiz content
        const response = await axios.get(
          `${apiUrls.stats.contentSpecificData}?contentId=${contentId}&contentType=content-random-quizz`,
        );

        // Note: axios interceptor in auth.ts auto-unwraps response.data.data to response.data
        const quizzContent = response.data;

        if (quizzContent) {
          const questions: Question[] = quizzContent.questions || [];
          const nbQuestions: number = quizzContent.nb_questions || questions.length;

          setQuizzData({
            questions,
            nb_questions: nbQuestions,
          });

          // Collect all unique image IDs from questions that use images
          const imageIds = new Set<number>();
          questions.forEach((q) => {
            if (q.imageAnswers) {
              q.answers.forEach((a) => {
                if (a.imageId) imageIds.add(a.imageId);
              });
            }
          });

          // Fetch images
          if (imageIds.size > 0) {
            const imagePromises = Array.from(imageIds).map((id) =>
              axios
                .get(`${apiUrls.medias.all}/${id}`)
                .then((res) => ({
                  id,
                  url: res.data?.data?.attributes?.s3_url || res.data?.s3_url || null,
                }))
                .catch(() => ({ id, url: null })),
            );

            const imageResults = await Promise.all(imagePromises);
            const urlMap = new Map<number, string>();
            imageResults.forEach((r) => {
              if (r.url) urlMap.set(r.id, r.url);
            });
            setImageUrls(urlMap);
          }
        }
      } catch (err) {
        console.error("Error fetching random quiz content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!quizzData || userAnswers.length === 0) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  // Render text-based question (like Quiz3)
  const renderTextQuestion = (
    question: Question,
    userAnswer: UserAnswer,
    questionNumber: number,
  ) => {
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Question header */}
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-500">Question {questionNumber}</span>
            {question.allowMultiple && (
              <span className="text-xs text-gray-400 italic">
                ({t("statsNew.interaction.quiz.multipleChoice")})
              </span>
            )}
          </div>
          <p className="mt-2 font-medium text-gray-900">{question.text}</p>
        </div>

        {/* Answers */}
        <div className="p-4 space-y-2">
          {question.answers.map((answer, aIndex) => {
            const wasSelected = userAnswer.answersIndex.includes(aIndex);
            const isRight = answer.is_right;
            const isCorrectAndSelected = isRight && wasSelected;
            const isWrongAndSelected = !isRight && wasSelected;
            const isMissedCorrect = isRight && !wasSelected;

            let bgColor = "bg-white border-gray-200";
            let textColor = "text-gray-700";

            if (isCorrectAndSelected) {
              bgColor = "bg-green-100 border-green-500 border-2";
              textColor = "text-green-800 font-medium";
            } else if (isWrongAndSelected) {
              bgColor = "bg-red-100 border-red-400";
              textColor = "text-red-800";
            } else if (isMissedCorrect) {
              bgColor = "bg-amber-50 border-amber-300 border-dashed";
              textColor = "text-amber-700";
            }

            return (
              <div
                key={aIndex}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${bgColor}`}
              >
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {isCorrectAndSelected && (
                    <Tooltip message={t("statsNew.interaction.quiz.tooltipCorrect")}>
                      <svg
                        className="h-5 w-5 text-green-600 cursor-help"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </Tooltip>
                  )}
                  {isWrongAndSelected && (
                    <Tooltip message={t("statsNew.interaction.quiz.tooltipWrong")}>
                      <svg
                        className="h-5 w-5 text-red-600 cursor-help"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Tooltip>
                  )}
                  {isMissedCorrect && (
                    <Tooltip message={t("statsNew.interaction.quiz.tooltipMissed")}>
                      <svg
                        className="h-5 w-5 text-amber-500 cursor-help"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </Tooltip>
                  )}
                  {!wasSelected && !isRight && <div className="w-5 h-5" />}
                </div>

                {/* Answer text */}
                <span className={`text-sm ${textColor}`}>{answer.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render image-based question (like QuizImageMultiple)
  const renderImageQuestion = (
    question: Question,
    userAnswer: UserAnswer,
    questionNumber: number,
  ) => {
    const answersWithImages: AnswerWithImage[] = question.answers.map((answer, index) => ({
      ...answer,
      index,
      imageUrl: answer.imageId ? imageUrls.get(answer.imageId) : undefined,
    }));

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Question header */}
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-500">Question {questionNumber}</span>
            {question.allowMultiple && (
              <span className="text-xs text-gray-400 italic">
                ({t("statsNew.interaction.quiz.multipleChoice")})
              </span>
            )}
          </div>
          <p className="mt-2 font-medium text-gray-900">{question.text}</p>
        </div>

        {/* Images grid */}
        <div className="p-4">
          <div className="grid grid-cols-4 gap-2">
            {answersWithImages.map((answer) => {
              const wasSelected = userAnswer.answersIndex.includes(answer.index);
              const isRight = answer.is_right;

              let status: "correct" | "wrong" | "missed" | "neutral";
              if (wasSelected && isRight) {
                status = "correct";
              } else if (wasSelected && !isRight) {
                status = "wrong";
              } else if (!wasSelected && isRight) {
                status = "missed";
              } else {
                status = "neutral";
              }

              return (
                <div
                  key={answer.index}
                  className={`relative rounded-lg border-2 ${
                    status === "correct"
                      ? "border-green-500"
                      : status === "wrong"
                      ? "border-red-500"
                      : status === "missed"
                      ? "border-yellow-500"
                      : "border-gray-200"
                  }`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                    {answer.imageUrl ? (
                      <img
                        src={answer.imageUrl}
                        alt={answer.text || `Image ${answer.index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <svg
                          className="h-8 w-8"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  {status !== "neutral" && (
                    <div
                      className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                        status === "correct"
                          ? "bg-green-500"
                          : status === "wrong"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                      title={
                        status === "correct"
                          ? t("statsNew.interaction.quiz.tooltipCorrect")
                          : status === "wrong"
                          ? t("statsNew.interaction.quiz.tooltipWrong")
                          : t("statsNew.interaction.quiz.tooltipMissed")
                      }
                    >
                      {status === "correct" ? (
                        <svg
                          className="h-3 w-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : status === "wrong" ? (
                        <svg
                          className="h-3 w-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      ) : (
                        <span className="text-white text-xs font-bold">!</span>
                      )}
                    </div>
                  )}

                  {/* Text label if available */}
                  {answer.text && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1 rounded-b-md">
                      <p className="text-xs text-white truncate">{answer.text}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Questions grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-4">
          {userAnswers.map((userAnswer, idx) => {
            const question = quizzData.questions[userAnswer.questionIndex];
            if (!question) return null;

            const questionNumber = idx + 1;

            return (
              <div key={idx}>
                {question.imageAnswers
                  ? renderImageQuestion(question, userAnswer, questionNumber)
                  : renderTextQuestion(question, userAnswer, questionNumber)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
              <svg
                className="h-3 w-3 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <span className="text-gray-600">{t("statsNew.interaction.quiz.tooltipCorrect")}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
              <svg
                className="h-3 w-3 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <span className="text-gray-600">{t("statsNew.interaction.quiz.tooltipWrong")}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">!</span>
            </div>
            <span className="text-gray-600">{t("statsNew.interaction.quiz.tooltipMissed")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomQuizzDetail;
